import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { auth } from './firebase';
import { signOut, reload } from 'firebase/auth';
import Login from './components/Login';
import Register from './components/Register';
import AppContent from './components/AppContent';
import CustomsChat from './components/CustomsChat';
import History from './components/History';
import Settings from './components/Settings';
import Home from './components/Home';
import TariffViewer from './components/TariffViewer';
import TCOViewer from './components/TCOViewer';
import AHECCViewer from './components/AHECCViewer';
import RefundReasonViewer from './components/RefundReasonViewer';
import Schedule4Viewer from './components/Schedule4Viewer';
import GSTExemptionsViewer from './components/GSTExemptionsViewer';
import CustomsDepotsViewer from './components/CustomsDepotsViewer';
import QuarantineDepotsViewer, { DataProvider } from './components/QuarantineDepotsViewer'; // Updated import
import PrecedentViewer from './components/PrecedentViewer';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import Subscription from './components/Subscription';
import SubscriptionManage from './components/SubscriptionManage';
import { checkSubscription } from './subscriptionUtils';
import VerifyEmail from './components/VerifyEmail';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import './App.css';

// ProtectedRoute component to handle subscription-based access control
const ProtectedRoute = ({ children, requiredPlan }) => {
    const navigate = useNavigate();
    const [access, setAccess] = useState(null);

    useEffect(() => {
        const verifyAccess = async () => {
            // Wait for auth state to stabilize
            if (!auth.currentUser) {
                toast.error(
                    <div className="toast-content">
                        Please log in to access this page.{' '}
                        <button onClick={() => navigate('/login')} className="toast-link">
                            Log in
                        </button>
                    </div>,
                    { autoClose: 5000, className: 'toastify' }
                );
                setAccess({ allowed: false, redirect: '/login' });
                return;
            }

            await reload(auth.currentUser);
            if (!auth.currentUser.emailVerified) {
                toast.error(
                    <div className="toast-content">
                        Please verify your email to use the app.{' '}
                        <button onClick={() => navigate('/verify-email')} className="toast-link">
                            Verify email
                        </button>
                    </div>,
                    { autoClose: 5000, className: 'toastify' }
                );
                setAccess({ allowed: false, redirect: '/verify-email' });
                return;
            }

            const { isAuthenticated, hasActiveSubscription, hasSubscription, subscriptionStatus, planLevel } = await checkSubscription();

            if (!isAuthenticated) {
                toast.error(
                    <div className="toast-content">
                        Please log in to access this page.{' '}
                        <button onClick={() => navigate('/login')} className="toast-link">
                            Log in
                        </button>
                    </div>,
                    { autoClose: 5000, className: 'toastify' }
                );
                setAccess({ allowed: false, redirect: '/login' });
                return;
            }

            if (!hasActiveSubscription) {
                if (hasSubscription) {
                    // User has a subscription (e.g., paused, canceled) → redirect to /subscription/manage
                    toast.error(
                        <div className="toast-content">
                            Your subscription is {subscriptionStatus || 'inactive'}. Please manage your subscription.{' '}
                            <button onClick={() => navigate('/subscription/manage')} className="toast-link">
                                Manage your subscription
                            </button>
                        </div>,
                        { autoClose: 5000, className: 'toastify' }
                    );
                    setAccess({ allowed: false, redirect: '/subscription/manage' });
                } else {
                    // No subscription → redirect to /subscription
                    toast.error(
                        <div className="toast-content">
                            No active subscription found. Please subscribe to continue.{' '}
                            <button onClick={() => navigate('/subscription')} className="toast-link">
                                Subscribe now
                            </button>
                        </div>,
                        { autoClose: 5000, className: 'toastify' }
                    );
                    setAccess({ allowed: false, redirect: '/subscription' });
                }
                return;
            }

            if (requiredPlan === 'Pro' && planLevel !== 'Pro') {
                toast.error(
                    <div className="toast-content">
                        A Pro plan is required to access this feature. Please upgrade your subscription.{' '}
                        <button onClick={() => navigate('/subscription')} className="toast-link">
                            Upgrade now
                        </button>
                    </div>,
                    { autoClose: 5000, className: 'toastify' }
                );
                setAccess({ allowed: false, redirect: '/subscription' });
                return;
            }

            setAccess({ allowed: true });
        };

        verifyAccess();
    }, [requiredPlan, navigate]);

    if (access === null) {
        return <div>Loading...</div>;
    }

    return access.allowed ? children : <Navigate to={access.redirect} replace />;
};

// EmailVerifiedRoute component to enforce email verification
const EmailVerifiedRoute = ({ children }) => {
    const navigate = useNavigate();
    const [verified, setVerified] = useState(null);

    useEffect(() => {
        const checkVerification = async () => {
            const user = auth.currentUser;
            if (!user) {
                toast.error(
                    <div className="toast-content">
                        Please log in to continue.{' '}
                        <button onClick={() => navigate('/login')} className="toast-link">
                            Log in
                        </button>
                    </div>,
                    { autoClose: 5000, className: 'toastify' }
                );
                setVerified({ allowed: false, redirect: '/login' });
                return;
            }
            await user.reload();
            if (user.emailVerified) {
                setVerified({ allowed: true });
            } else {
                toast.error(
                    <div className="toast-content">
                        Please verify your email to proceed.{' '}
                        <button onClick={() => navigate('/verify-email')} className="toast-link">
                            Verify email
                        </button>
                    </div>,
                    { autoClose: 5000, className: 'toastify' }
                );
                setVerified({ allowed: false, redirect: '/verify-email' });
            }
        };

        checkVerification();
        const interval = setInterval(checkVerification, 5000);
        return () => clearInterval(interval);
    }, [navigate]);

    if (verified === null) {
        return <div>Loading...</div>;
    }

    return verified.allowed ? children : <Navigate to={verified.redirect} replace />;
};

function App() {
    const [user, setUser] = useState(null);
    const navigate = useNavigate();
    const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds
    const MAX_SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    // Inactivity logout logic
    const handleLogout = useCallback(() => {
        signOut(auth)
            .then(() => {
                setUser(null);
                if (typeof sessionStorage !== 'undefined') {
                    sessionStorage.removeItem('lastActivity');
                    sessionStorage.removeItem('initialLoginTime');
                }
                if (typeof localStorage !== 'undefined') {
                    localStorage.removeItem('user');
                }
                navigate('/login');
                toast.info(
                    <div className="toast-content">
                        Logged out due to inactivity or session expiration. Please log in again.{' '}
                        <button onClick={() => navigate('/login')} className="toast-link">
                            Log in
                        </button>
                    </div>,
                    { autoClose: 5000, className: 'toastify' }
                );
            })
            .catch((err) => {
                console.error('Logout error:', err);
                toast.error(
                    <div className="toast-content">
                        Logout failed. Please try again.{' '}
                        <button onClick={() => navigate('/login')} className="toast-link">
                            Try again
                        </button>
                    </div>,
                    { autoClose: 5000, className: 'toastify' }
                );
            });
    }, [navigate]);

    const resetInactivityTimer = useCallback(() => {
        clearTimeout(window.inactivityWarning);
        clearTimeout(window.inactivityLogout);
        window.inactivityWarning = setTimeout(() => {
            toast.warn(
                <div className="toast-content">
                    Session will expire in 2 minutes due to inactivity. Click anywhere to continue.
                </div>,
                { autoClose: 120000, className: 'toastify' }
            );
        }, INACTIVITY_TIMEOUT - 2 * 60 * 1000);
        window.inactivityLogout = setTimeout(handleLogout, INACTIVITY_TIMEOUT);
        return () => {
            clearTimeout(window.inactivityWarning);
            clearTimeout(window.inactivityLogout);
        };
    }, [INACTIVITY_TIMEOUT, handleLogout]);

    const [isAuthLoading, setIsAuthLoading] = useState(true);
    
    useEffect(() => {
        const publicRoutes = ['/', '/terms', '/privacy', '/how-to-use', '/login', '/register', '/verify-email'];
        const unsubscribe = auth.onAuthStateChanged((currentUser) => {
            setIsAuthLoading(false);
            const currentPath = window.location.pathname;
            if (!currentUser) {
                setUser(null);
                if (typeof sessionStorage !== 'undefined') {
                    sessionStorage.removeItem('lastActivity');
                    sessionStorage.removeItem('initialLoginTime');
                }
                if (typeof localStorage !== 'undefined') {
                    localStorage.removeItem('user');
                }
                if (!publicRoutes.includes(currentPath)) {
                    toast.error(
                        <div className="toast-content">
                            Please log in to access this page.{' '}
                            <button onClick={() => navigate('/login')} className="toast-link">
                                Log in
                            </button>
                        </div>,
                        { autoClose: 5000, className: 'toastify' }
                    );
                    navigate('/login');
                }
                return;
            }
            setUser(currentUser);
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem('initialLoginTime', Date.now().toString());
                sessionStorage.setItem('lastActivity', Date.now().toString());
            }
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem('user', JSON.stringify(currentUser));
            }
            resetInactivityTimer();
        });

        // Dark mode setup
        if (typeof localStorage !== 'undefined') {
            const storedDarkMode = localStorage.getItem('darkMode') === 'true';
            document.body.classList.toggle('dark-mode', storedDarkMode);
        }

        // Sync dark mode across tabs
        const handleStorageChange = (e) => {
            if (e.key === 'darkMode' && typeof localStorage !== 'undefined') {
                const isDark = localStorage.getItem('darkMode') === 'true';
                document.body.classList.toggle('dark-mode', isDark);
            }
        };
        window.addEventListener('storage', handleStorageChange);

        // Inactivity listener
        const handleActivity = () => {
            if (user && typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem('lastActivity', Date.now().toString());
                resetInactivityTimer();
            }
        };

        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('keydown', handleActivity);
        window.addEventListener('click', handleActivity);

        // Check last activity and session duration on load
        if (typeof sessionStorage !== 'undefined' && typeof localStorage !== 'undefined') {
            const lastActivity = sessionStorage.getItem('lastActivity');
            const initialLoginTime = sessionStorage.getItem('initialLoginTime');
            if (user) {
                if (!lastActivity || !initialLoginTime) {
                    handleLogout();
                } else {
                    const timeSinceLogin = Date.now() - parseInt(initialLoginTime, 10);
                    const timeElapsed = Date.now() - parseInt(lastActivity, 10);
                    if (timeSinceLogin >= MAX_SESSION_DURATION || timeElapsed >= INACTIVITY_TIMEOUT) {
                        handleLogout();
                    } else {
                        const remainingTime = Math.min(INACTIVITY_TIMEOUT - timeElapsed, MAX_SESSION_DURATION - timeSinceLogin);
                        const timeout = setTimeout(handleLogout, remainingTime);
                        return () => clearTimeout(timeout);
                    }
                }
            }
        }

        // Check history expiry
        if (typeof localStorage !== 'undefined') {
            const historyData = JSON.parse(localStorage.getItem('customspro_classifications') || '{}');
            if (historyData.expiry && Date.now() > historyData.expiry) {
                localStorage.removeItem('customspro_classifications');
            }
        }

        return () => {
            unsubscribe();
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keydown', handleActivity);
            window.removeEventListener('click', handleActivity);
        };
    }, [user, INACTIVITY_TIMEOUT, handleLogout, resetInactivityTimer, MAX_SESSION_DURATION, navigate]);

    if (isAuthLoading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="app">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} closeOnClick pauseOnHover />
            <Routes>
                <Route path="/" element={<Home user={user} />} />
                <Route
                    path="/login"
                    element={
                        user?.emailVerified ? (
                            <Navigate to="/app" replace />
                        ) : user ? (
                            <Navigate to="/verify-email" replace />
                        ) : (
                            <Login setUser={setUser} />
                        )
                    }
                />
                <Route
                    path="/register"
                    element={
                        user?.emailVerified ? (
                            <Navigate to="/app" replace />
                        ) : user ? (
                            <Navigate to="/verify-email" replace />
                        ) : (
                            <Register setUser={setUser} />
                        )
                    }
                />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route
                    path="/app"
                    element={
                        <ProtectedRoute>
                            <AppContent user={user} />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/customs-chat"
                    element={
                        <ProtectedRoute>
                            <CustomsChat user={user} />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/history"
                    element={
                        <ProtectedRoute requiredPlan="Pro">
                            <History />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/settings"
                    element={
                        <ProtectedRoute>
                            <Settings />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/tariff-viewer"
                    element={
                        <ProtectedRoute requiredPlan="Pro">
                            <TariffViewer />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/TCO-viewer"
                    element={
                        <ProtectedRoute requiredPlan="Pro">
                            <TCOViewer />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/precedent-viewer"
                    element={
                        <ProtectedRoute requiredPlan="Pro">
                            <PrecedentViewer />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/ahecc-viewer"
                    element={
                        <ProtectedRoute requiredPlan="Pro">
                            <AHECCViewer />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/schedule4-viewer"
                    element={
                        <ProtectedRoute requiredPlan="Pro">
                            <Schedule4Viewer />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/refund-reason-viewer"
                    element={
                        <ProtectedRoute requiredPlan="Pro">
                            <RefundReasonViewer />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/gst-exemptions-viewer"
                    element={
                        <ProtectedRoute requiredPlan="Pro">
                            <GSTExemptionsViewer />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/customs-depots-viewer"
                    element={
                        <ProtectedRoute requiredPlan="Pro">
                            <CustomsDepotsViewer />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/quarantine-depots-viewer"
                    element={
                        <ProtectedRoute requiredPlan="Pro">
                          <DataProvider> {/* Add DataProvider here */}
                            <QuarantineDepotsViewer />
                          </DataProvider>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/subscription"
                    element={
                        <EmailVerifiedRoute>
                            <Subscription />
                        </EmailVerifiedRoute>
                    }
                />
                <Route
                    path="/subscription/manage"
                    element={
                        <EmailVerifiedRoute>
                            <SubscriptionManage />
                        </EmailVerifiedRoute>
                    }
                />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route
                    path="*"
                    element={
                        <Navigate
                            to={!user ? '/login' : !user.emailVerified ? '/verify-email' : '/app'}
                            replace
                        />
                    }
                />
            </Routes>
        </div>
    );
}

const AppWithRouter = () => (
    <Router>
        <App />
    </Router>
);

export default AppWithRouter;